import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import ImageCropper from "@/components/ImageCropper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowRight, Upload, Trash2, KeyRound, LogOut, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
    const { user, refresh, logout } = useAuth();
    const nav = useNavigate();
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [cropSrc, setCropSrc] = useState(null);
    const [cropOpen, setCropOpen] = useState(false);
    const [cur, setCur] = useState(""); const [np, setNp] = useState(""); const [cp, setCp] = useState("");
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    const [showInLb, setShowInLb] = useState(user?.show_in_leaderboard !== false);

    const toggleLb = async (next) => {
        setShowInLb(next);
        try {
            await api.post("/me/leaderboard-privacy", { show_in_leaderboard: next });
            toast.success(next ? "سيظهر اسمك في لوحة الشرف" : "أصبحت خصوصية اسمك مفعّلة");
            await refresh();
        } catch (e) { toast.error(formatApiError(e)); setShowInLb(!next); }
    };

    const onPick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => { setCropSrc(reader.result); setCropOpen(true); };
        reader.readAsDataURL(f);
        if (fileRef.current) fileRef.current.value = "";
    };

    const doUpload = async (file) => {
        setUploading(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            await api.post("/me/avatar", fd);
            toast.success("تم تحديث صورتك الشخصية");
            await refresh();
        } catch (er) { toast.error(formatApiError(er)); }
        finally { setUploading(false); }
    };

    const removeAvatar = async () => {
        try { await api.delete("/me/avatar"); toast.success("تمت الإزالة"); await refresh(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const changePassword = async (e) => {
        e.preventDefault(); setErr("");
        if (np.length < 6) return setErr("كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل");
        if (np !== cp) return setErr("كلمة المرور وتأكيدها غير متطابقين");
        setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: cur, new_password: np });
            toast.success("تم تحديث كلمة المرور");
            setCur(""); setNp(""); setCp("");
        } catch (er) { setErr(formatApiError(er)); }
        finally { setBusy(false); }
    };

    const backPath = user?.role === "student" ? "/student" : user?.role === "parent" ? "/parent" : user?.role === "trainer" ? "/trainer" : "/admin";

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => nav(backPath)} className="text-white/80 hover:text-white hover:bg-white/10"><ArrowRight className="w-4 h-4 ml-1" /> رجوع</Button>
                        <BrandLogo size={36} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="text-white/80 hover:text-white hover:bg-white/10"><LogOut className="w-4 h-4 ml-1" /> خروج</Button>
                </div>
            </header>

            <main className="max-w-3xl mx-auto p-6 space-y-5">
                <div>
                    <h1 className="text-2xl font-bold text-[#0F172A]">حسابي</h1>
                    <p className="text-sm text-slate-500 mt-1">صورتك الشخصية وكلمة المرور</p>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center gap-5">
                        <Avatar url={user?.avatar_url} name={user?.full_name} seed={user?.avatar_seed} role={user?.role} size={96} />
                        <div className="flex-1">
                            <div className="text-lg font-bold text-[#0F172A]">{user?.full_name}</div>
                            <div className="text-sm text-slate-500 font-mono-num" dir="ltr">{user?.username}</div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="btn-upload-avatar" className="bg-[#1B2A4E] hover:bg-[#0F172A]">
                                    <Upload className="w-4 h-4 ml-1" /> {uploading ? "جارٍ الرفع..." : "تحميل صورة"}
                                </Button>
                                {user?.avatar_url && <Button variant="outline" onClick={removeAvatar} data-testid="btn-remove-avatar" className="text-[#C8102E] border-[#FECACA] hover:bg-[#FFF1F2]"><Trash2 className="w-4 h-4 ml-1" /> إزالة</Button>}
                                <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="avatar-input" />
                            </div>
                            <div className="text-xs text-slate-400 mt-2">اختاري صورة → قصي بشكل دائري وقربي/بعدي وأديريها → احفظي</div>
                        </div>
                    </div>
                </div>

                <ImageCropper open={cropOpen} onOpenChange={setCropOpen} imageSrc={cropSrc} aspect={1} onCropped={doUpload} title="قص صورتك الشخصية" />

                {user?.role === "student" && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Trophy className="w-4 h-4 text-amber-500" />
                            <div className="font-semibold text-[#0F172A]">خصوصية لوحة الشرف</div>
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="toggle-leaderboard-privacy"
                                checked={showInLb}
                                onChange={(e) => toggleLb(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-[#1B2A4E]"
                            />
                            <div className="text-sm">
                                <div className="font-medium text-[#0F172A]">إظهار اسمي الكامل في لوحة شرف الفصل</div>
                                <div className="text-xs text-slate-500 mt-1">
                                    عند الإيقاف، يظهر اسمك الأول فقط لباقي الطالبات، لكن ترتيبك الفعلي يبقى محفوظاً لكِ.
                                </div>
                            </div>
                        </label>
                    </div>
                )}

                <form onSubmit={changePassword} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="change-pw-form">
                    <div className="flex items-center gap-2 mb-2">
                        <KeyRound className="w-4 h-4 text-[#C8102E]" />
                        <div className="font-semibold text-[#0F172A]">تغيير كلمة المرور</div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div><Label>الحالية</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1" data-testid="pw-current" /></div>
                        <div><Label>الجديدة</Label><Input type="password" value={np} onChange={(e) => setNp(e.target.value)} className="mt-1" data-testid="pw-new" /></div>
                        <div><Label>تأكيد</Label><Input type="password" value={cp} onChange={(e) => setCp(e.target.value)} className="mt-1" data-testid="pw-confirm" /></div>
                    </div>
                    {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
                    <div className="flex justify-end">
                        <Button type="submit" disabled={busy} data-testid="btn-save-pw" className="bg-[#1B2A4E] hover:bg-[#0F172A]">{busy ? "جارٍ الحفظ..." : "حفظ"}</Button>
                    </div>
                </form>
            </main>
        </div>
    );
}
